import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, LayoutDashboard, Zap, Shield, Database, GitBranch, Activity,
  Key, Laptop, Cloud, Lock, DollarSign, Users, FileText, AlertTriangle,
  CheckCircle, ChevronRight, MessageSquare, Search, BookOpen
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { MarketingLayout } from '../../components/marketing/MarketingLayout';

const domains = [
  {
    icon: LayoutDashboard,
    title: 'Control Tower',
    path: '/control-tower',
    description: 'Unified operations dashboard for all IT lifecycle cases. Monitor active cases, agent health, and system metrics.',
    features: ['Case management', 'Agent orchestration', 'Real-time metrics', 'System health']
  },
  {
    icon: MessageSquare,
    title: 'Resolve Requests',
    path: '/product/resolve-requests',
    description: 'Multi-channel request intake from Slack, Teams, email, and web portal. Automatic classification and routing.',
    features: ['Multi-channel intake', 'Auto-classification', 'Intelligent routing', 'Follow-up questions']
  },
  {
    icon: Key,
    title: 'Access Management',
    path: '/product/access-management',
    description: 'Full identity lifecycle from request through revocation. Includes periodic access reviews and break-glass procedures.',
    features: ['Access requests', 'Approval workflows', 'Periodic reviews', 'Time-bound grants']
  },
  {
    icon: Zap,
    title: 'Automation Builder',
    path: '/product/automation-builder',
    description: 'Build and manage automation workflows with Action Bundles. Policy Studio provides governance and simulation.',
    features: ['Action Bundles', 'Policy Studio', 'Template library', 'Version control']
  },
  {
    icon: BookOpen,
    title: 'Knowledge',
    path: '/product/knowledge',
    description: 'Ingest documentation from Notion, Confluence, SharePoint. Search and cite sources. Turn answers into actions.',
    features: ['Multi-source ingestion', 'Semantic search', 'Source citations', 'Action conversion']
  },
  {
    icon: Laptop,
    title: 'Endpoints',
    path: '/endpoints',
    description: 'Device compliance monitoring and remediation. Integration with MDM platforms for automated enforcement.',
    features: ['Compliance monitoring', 'MDM integration', 'Automated remediation', 'Fleet visibility']
  },
  {
    icon: Cloud,
    title: 'SaaS Governance',
    path: '/saas',
    description: 'Application access management and spend tracking. Identify shadow IT and reclaim unused licenses.',
    features: ['Access governance', 'License management', 'Spend tracking', 'Shadow IT detection']
  },
  {
    icon: Lock,
    title: 'Security Response',
    path: '/security',
    description: 'Threat containment workflows for suspicious logins, compromised accounts, and malware detection.',
    features: ['Incident response', 'Account lockout', 'Session revocation', 'Evidence collection']
  },
  {
    icon: Database,
    title: 'Asset Graph',
    path: '/assets',
    description: 'Complete inventory of users, devices, applications, and their relationships. Single source of truth.',
    features: ['User inventory', 'Device inventory', 'Application catalog', 'Relationship mapping']
  },
  {
    icon: GitBranch,
    title: 'Configuration Intelligence',
    path: '/services',
    description: 'Service dependency mapping and configuration drift detection. Track changes across your infrastructure.',
    features: ['Dependency mapping', 'Drift detection', 'Change tracking', 'Impact analysis']
  },
  {
    icon: DollarSign,
    title: 'Cost Intelligence',
    path: '/cost',
    description: 'Spend attribution across teams and applications. Identify optimization opportunities and track savings.',
    features: ['Spend attribution', 'Cost allocation', 'Optimization alerts', 'Savings tracking']
  },
  {
    icon: FileText,
    title: 'Ticketing & ITSM',
    path: '/product/itsm',
    description: 'Orchestration layer on top of existing ITSM. Two-way sync with ServiceNow, Jira Service Management.',
    features: ['ITSM integration', 'Ticket sync', 'Evidence linking', 'Approval mirroring']
  }
];

const architectureSteps = [
  {
    step: '1',
    title: 'Request Intake',
    description: 'Requests arrive via Slack, Teams, email, portal, or API. Classification determines the case type and required information.'
  },
  {
    step: '2',
    title: 'Control Tower',
    description: 'Cases are created in Control Tower. Policy engine evaluates automation eligibility and approval requirements.'
  },
  {
    step: '3',
    title: 'Action Bundles',
    description: 'Pre-built automation sequences execute approved actions. Each step is logged with full evidence capture.'
  },
  {
    step: '4',
    title: 'Target Systems',
    description: 'Connectors execute actions in IdP, HRIS, MDM, and SaaS systems. Read-only by default; writes require policy approval.'
  },
  {
    step: '5',
    title: 'Audit & Evidence',
    description: 'Complete audit trail with before/after snapshots. Evidence packs for compliance and post-incident review.'
  }
];

const differentiators = [
  {
    title: 'Read-Only First',
    description: 'All connectors start read-only. Write operations require explicit policy enablement with approval gates.'
  },
  {
    title: 'Policy-Driven Automation',
    description: 'Automation boundaries defined by policy, not code. Simulate changes before deployment. Rollback on failure.'
  },
  {
    title: 'Complete Audit Trail',
    description: 'Every action logged with full context. Evidence packs include before/after state. 7-year retention standard.'
  },
  {
    title: 'Observe-to-Auto Rollout',
    description: 'Start by observing current processes. Propose actions for human approval. Graduate to full automation over time.'
  }
];

export const ProductOverview = () => {
  return (
    <MarketingLayout>
      <div className="min-h-screen">
        {/* Hero */}
        <section className="py-16 px-6 bg-white border-b border-border">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 tracking-tight">
              IT Lifecycle Management Platform
            </h1>
            <p className="text-lg text-muted-foreground mb-8 max-w-3xl leading-relaxed">
              Automate identity provisioning, access management, endpoint compliance, and security response.
              Enforce governance policies. Maintain complete audit evidence.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Button asChild size="default">
                <Link to="/demo/request">
                  Request Demo
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild size="default" variant="outline">
                <Link to="/control-tower">Open Control Tower</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 px-6 bg-secondary/30">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">How It Works</h2>
            <div className="grid md:grid-cols-5 gap-4">
              {architectureSteps.map((step, idx) => (
                <div key={idx} className="relative">
                  <div className="bg-white border border-border rounded-lg p-4 h-full">
                    <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold mb-3">
                      {step.step}
                    </div>
                    <h3 className="text-sm font-semibold text-foreground mb-2">{step.title}</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">{step.description}</p>
                  </div>
                  {idx < architectureSteps.length - 1 && (
                    <div className="hidden md:block absolute top-10 -right-2 w-4 text-muted-foreground">
                      <ChevronRight className="w-4 h-4" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Product Areas */}
        <section className="py-16 px-6 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">Platform Capabilities</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {domains.map((domain, idx) => (
                <Link
                  key={idx}
                  to={domain.path}
                  className="group block bg-white border border-border rounded-lg p-5 hover:border-primary/50 transition-colors"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                      <domain.icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">
                        {domain.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{domain.description}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {domain.features.map((feature, fidx) => (
                      <span
                        key={fidx}
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-secondary text-xs text-muted-foreground rounded-md"
                      >
                        <CheckCircle className="w-3 h-3" />
                        {feature}
                      </span>
                    ))}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Differentiators */}
        <section className="py-16 px-6 bg-secondary/30 border-t border-border">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">What Sets Us Apart</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {differentiators.map((diff, idx) => (
                <div key={idx} className="bg-white border border-border rounded-lg p-5">
                  <h3 className="text-base font-semibold text-foreground mb-2">{diff.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{diff.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-6 bg-white border-t border-border">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-semibold text-foreground mb-4">Start Your Evaluation</h2>
            <p className="text-muted-foreground mb-8">
              Deploy a pilot in 2 weeks. Start with read-only connectors, then graduate to automation with governance controls.
            </p>
            <div className="flex justify-center gap-3">
              <Button asChild size="lg">
                <Link to="/demo/request">
                  Request Demo
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/pilot">View Pilot Plan</Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
};

export default ProductOverview;
